import { IMetricParameterSet, IMetrics } from "../PA.Metric.Services/PA.Metrics.IF";

export enum ModelingEngineMetricLevels {
    ByTotal = "Total",
    ByCustomer = "Customer",
    ByPromotedGroup = "ProductGroup",
    ByCustomerPromotedGroup = "CustomerProductGroup",
    ByCustomerPromotedGroupPeriod = "CustomerProductGroupPeriod",
    ByPromotedGroupPeriod = "ProductGroupPeriod",
}

export enum ModelingSessionStatus {
    NotLoaded = "NotLoaded",
    ValidatingScopeAndLoadingData = "ValidatingScopeAndLoadingData",
    LoadedAndReady = "LoadedAndReady",
    ExecutingSolver = "ExecutingSolver",
    WritingMetrics = "WritingMetrics",
    DoneWithSuccess = "DoneWithSuccess",
    DoneWithError = "DoneWithError",
}

export interface IModelingSessionServiceRequest {
    RequestType: string;
    RequestOptions: any;
    wbModelingSessionId?: number;
    wbModelingSessionTrialId?: number;
    GUID: string;
    bAllowLongRunning: boolean; //indicates whether the server should allow the Java call to take longer than the default timeout
    metricParameterSet?: IMetricParameterSet;
}

export interface IModelingSessionServiceCalculateTrialOptions {
    wbAuthUsrId?: number;
    FitMetricLevels?: string[]; // ignore for now
    PredictorMetrics?: number[]; // ignore for now
}

export interface IModelingSessionServiceCalculateSessionOptions {
    wbAuthUsrId?: number;
    SolveWithoutModelSessionMetricTags?: boolean;
    SolveWithoutNoDataTags?: boolean;
    RecalculateConstants?: boolean;
    CalculateIntelligentIntercept?: boolean;
    FitMetricLevels?: string[]; // ignore for now
    PredictorMetrics?: number[]; // ignore for now
}

export interface IModelingSessionServiceCalculateReferenceValuesOptions {
    wbAuthUsrId?: number;
    options?: any;
}

const modelSolverStatuses = ["Ready", "Executing", "Saving", "NotLoaded", "Loading"] as const;
export type ModelSolverStatusType = (typeof modelSolverStatuses)[number];
export function isValidModelSolverStatus(value: unknown): value is ModelSolverStatusType {
    if (typeof value !== "string") return false;
    return modelSolverStatuses.includes(value as ModelSolverStatusType);
}

export interface IModelingSessionServiceBaseResponse {
    wbModelingSessionId: number;
    GUID: string;
    stageCount: number;
    statusType: "Ready" | "Executing" | "Saving" | "NotLoaded" | "Loading";
    percentComplete: number; // integer from 0-100
    stagesCompleted: number;
    wbModelingSessionTrialId: number;
    errors: string[];
    warnings: string[];
    infoMessages: string[];
    isSuccess: boolean;  // This dup was introduced..
    bSuccess: boolean
}

export interface IModelingSessionServiceResponse extends IModelingSessionServiceBaseResponse {
    results: string | undefined;
}

export interface IModelingSessionServiceSummarizeTrialMetricsOptions {
    wbAuthUsrId?: number;
}

export interface ITrialFitMetricSummary {
    PREDICTED_UNITS: number;
    PREDICTED_RTLSLS: number;
    PREDICTED_BASE_UNITS: number;
    PREDICTED_BASE_RTLSLS: number;
    PREDICTED_INCR_UNITS: number;
    PREDICTED_INCR_RTLSLS: number;

    TARGET_OBSERVATION_UNITS: number;
    TARGET_OBSERVATION_RTLSLS: number;

    OBSERVATION_COUNT?: number;
    OBSERVATION_BASE_COUNT?: number;
    OBSERVATION_INCR_COUNT?: number;
    OBSERVATION_BASE_PCNT: number;
    OBSERVATION_PROMOTED_PCNT: number;

    RESIDUAL_MEAN?: number;
    RESIDUAL_STD_DEV?: number;
    RESIDUAL_MEAN_P1_STDDEV?: number;
    RESIDUAL_MEAN_M1_STDDEV?: number;
}

export interface ITrialFitMetricSummarySet {
    [id: string]: ITrialFitMetricSummary;
}

export interface IModelingSessionServiceSummarizeTrialFitMetricsResponse extends IModelingSessionServiceBaseResponse {
    results: ITrialFitMetricSummarySet;
}

export interface IModelingSessionServiceDatasetLeafResponse extends IModelingSessionServiceBaseResponse {
    results: {
        wbCustId: number;
        wbPrdId: number;
    }[];
}

export interface IModelingSessionServiceReferenceValuesResponse extends IModelingSessionServiceBaseResponse {
    results: {
        [wbCustId: number]: {
            [wbProdId: number]: {
                [wbPredictorId: number]: number;
            };
        };
    }[];
}

export interface IModelingSessionServiceStatusResponse extends IModelingSessionServiceBaseResponse {
    results: IModelingSessionStatus;
}

export interface IModelingSessionStatus {
    statusType: ModelSolverStatusType;
    numberOfTrialsExecuted: number;
    startDTTS: number;
    bestObjectiveFunctionValue: number;
    numberOfPartitions: number;
    numberOfPartitionsSolved: number;
}

export interface IDatasetObservations {
    observations: {
        wbCustId: number;
        wbPrdId: number;
        wbPeriodId: number;
        targetValue: number;
        predictors: { [wbPredictorId: number]: number | boolean };
    }[];
}

export interface IModelingSessionServiceExportDatasetResponse extends IModelingSessionServiceResponse {
    results: string; //filepath to file that will be exported
}

export interface IModelingSessionServiceOutlierResponse extends IModelingSessionServiceBaseResponse {
    bSuccess: boolean;
}

export interface IModelingSessionService {
    //Calculate Reference values
    CalculateReferenceValues(
        wbModelingSessionId: number,
        opts?: IModelingSessionServiceCalculateReferenceValuesOptions,
    ): Promise<IModelingSessionServiceReferenceValuesResponse>;

    // Calculate Trial
    CalculateTrial(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateTrialOptions,
    ): Promise<IModelingSessionServiceResponse>;

    // Calculate Session
    CalculateSession(
        wbModelingSessionId: number,
        opts?: IModelingSessionServiceCalculateSessionOptions,
    ): Promise<IModelingSessionServiceResponse>;

    GetSessionStatus(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceResponse>;

    StopSession(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceResponse>;

    // Summarize Metrics
    SummarizeTrialFitMetrics(
        wbModelingSessionTrialId: number,
        metricParameterSet: IMetricParameterSet,
        opts?: IModelingSessionServiceSummarizeTrialMetricsOptions,
    ): Promise<IModelingSessionServiceSummarizeTrialFitMetricsResponse>;

    ExportSessionDataset(
        wbModelingSessionId: number,
        opts?: any,
    ): Promise<IModelingSessionServiceExportDatasetResponse>;

    ExportTrialDatasetAndPredictions(
        wbModelingSessionId: number,
        opts?: any,
    ): Promise<IModelingSessionServiceExportDatasetResponse>;

    //Get Dataset
    GetDatasetLeafItems(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceDatasetLeafResponse>;

    //Calculate Quality Sub Tabs
    CalculateOutliers(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse>;

    CalculateSkew(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse>;

    CalculateBias(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse>;

    CalculateSignificance(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse>;
}

export interface IModelingSessionServiceCalculateQualitySubTabOptions {
    ConfidenceInterval?: number;
    SubTabName?: string;
}
